import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { TestCardComponent } from "./test/test.component";
import { ResultDimensionComponent } from "./result-dimension/result-dimension.component";
import { TestsService } from "../../../../core/services/tests.service";
import { LoaderService } from "../../../../core/shared/services/loader.service";
import { AuthService } from "../../../../core/services/auth.service";
import { AuthUser } from "../../../../core/models/auth/auth.interface";
import { TestKnowRequest } from "../../../../core/models/test.model";
import { TEST_TYPES } from "../../../../core/shared/constants";
import { BaseComponent } from "../../../../core/shared/base.component";
import { ConfigurationService } from "../../../../core/services/configuration.service";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Router, ActivatedRoute } from "@angular/router";
import { environment } from "../../../../../environments/environment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Interfaces para tipado fuerte
interface TestDimension {
  name: string;
  area: {
    name: string;
  };
  completed: boolean;
  questions: Question[];
}

interface Question {
  name: string;
  title: string;
  answer?: {
    valueNow: number;
    valueFuture: number;
  };
}

interface Test {
  _id: string;
  id?: string;
  type: any;
  user: any;
  dimensions: TestDimension[];
  questions: any[];
  completed: boolean;
  progress: number;
  totalProgress: number;
}

interface Subdimension {
  name: string;
  originalName: string;
  areaName: string;
  progress: number;
  id: string;
  dimension: TestDimension;
}

interface StepperStep {
  completed: boolean;
  active: boolean;
  pending: boolean;
}

interface CurrentQuestion {
  name: string;
  text: string;
  score: number;
  originalIndex: number;
}

interface ResultsData {
  subdimensionName: string;
  tableData: any[];
  averages: {
    presentAnalysis: number;
    futureAnalysis: number;
    gapToClose: number;
  };
  recommendedReadings: any[];
}

@Component({
  selector: "app-test-knowledge",
  standalone: true,
  imports: [CommonModule, TestCardComponent, ResultDimensionComponent],
  templateUrl: "./test-knowledge.component.html",
  styleUrl: "./test-knowledge.component.scss",
})
export class TestKnowledgeComponent extends BaseComponent implements OnInit {
  // Inyección de dependencias
  private readonly loader = inject(LoaderService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly testService: TestsService = inject(TestsService);
  private readonly configService = inject(ConfigurationService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  constructor() {
    super();
    console.log('TestKnowledgeComponent constructor ejecutado');
  }

  // Propiedades del estado de la aplicación
  testInProgress: boolean = false;
  testSelected: Test | null = null;
  selectedSubdimension: any = null;
  currentStep: number = 1;
  currentSatisfaction: number = 1;
  futureSatisfaction: number = 1;
  subdimensions: Subdimension[] = [];
  
  // Propiedades para las preguntas actuales
  currentQuestions: CurrentQuestion[] = [];
  
  // Propiedades para cachear el stepper
  private cachedStepperSteps: StepperStep[] = [];
  private lastStepperCacheKey: string = '';
  
  // Propiedades para el estado de guardado
  isSaving: boolean = false;
  saveError: string | null = null;
  isCompletingSubdimension: boolean = false;
  isClearingResults: boolean = false;
  showClearButton: boolean = false; // Se muestra solo si la URL contiene ?function=clear
  
  // Propiedades para modal y resultados
  showCompletionModal: boolean = false;
  completedSubdimension: any = null;
  showResultsView: boolean = false;
  resultsData: ResultsData | null = null;
  showIncompleteDimensionModal: boolean = false;
  
  // Propiedades para desplegar matrices de resultados
  showResultsMatrices: boolean = false;
  expandedDimensions: Set<string> = new Set();

  // Propiedades del componente
  tests: Test[] = [];
  testsCompleted: number = 0;
  dimensionsCompleted: number = 0;
  progressSteps: any[] = [];
  authUser: AuthUser | null = null;

  ngOnInit(): void {
    try {
      console.log('Iniciando TestKnowledgeComponent...');
      
      // Verificar si la URL contiene el parámetro function=clear
      this.route.queryParams.subscribe(params => {
        this.showClearButton = params['function'] === 'clear';
        console.log('Parámetro function en URL:', params['function']);
        console.log('Mostrar botón de limpiar:', this.showClearButton);
      });
      
      this.initializeComponent();
    } catch (error) {
      console.error('Error al inicializar TestKnowledgeComponent:', error);
      this.error('Error al inicializar el componente. Por favor, recarga la página.');
    }
  }

  /**
   * Inicializa el componente
   */
  private initializeComponent(): void {
    try {
      console.log('Inicializando componente...');
      console.log('Servicios disponibles:', {
        loader: !!this.loader,
        authService: !!this.authService,
        testService: !!this.testService,
        configService: !!this.configService,
        router: !!this.router
      });
      
      const user = this.authService.getUser();
      console.log('Usuario obtenido del authService:', user);
      
      this.authUser = user as unknown as AuthUser;
      console.log('AuthUser asignado:', this.authUser);
      
      if (!this.authUser) {
        console.error('No hay usuario autenticado, redirigiendo al login');
        this.router.navigate(['/login']);
        return;
      }

      // El usuario viene con _id del backend, lo mapeamos a id
      const userWithId = this.authUser as any;
      if (!this.authUser.id && userWithId._id) {
        this.authUser.id = userWithId._id;
        console.log('Mapeado _id a id:', this.authUser.id);
      }

      if (!this.authUser.id) {
        console.error('AuthUser no tiene id ni _id:', this.authUser);
      return;
    }

      console.log('Usuario válido, cargando tests...');
      this.loadTests();
      this.initializeProgressSteps();
      
      console.log('Componente inicializado correctamente');
    } catch (error) {
      console.error('Error en initializeComponent:', error);
      this.error('Error al inicializar el componente. Por favor, recarga la página.');
    }
  }

  /**
   * Carga los tests del usuario
   */
  private loadTests(): void {
    if (!this.authUser || !this.authUser.id) {
      console.error('AuthUser o authUser.id no está disponible:', this.authUser);
      this.loader.hide();
      return;
    }

    console.log('Cargando tests para userId:', this.authUser.id);
    this.loader.show();
    this.testService.find(this.authUser.id, TEST_TYPES.Autoconocimiento.id)
      .subscribe({
        next: (res: any) => {
          if (res.data && Array.isArray(res.data) && res.data.length > 0) {
            this.tests = this.initializeTests(res.data);
            this.updatePorcentTests();
          } else {
            // Si no hay tests, inicializar como array vacío
            this.tests = [];
            console.log('No se encontraron tests para el usuario');
          }
          this.loader.hide();
        },
        error: (err: any) => {
          console.error('Error al cargar tests:', err);
          this.tests = [];
          this.loader.hide();
        }
      });
  }

  /**
   * Inicializa los tests eliminando duplicados
   */
  private initializeTests(tests: Test[]): Test[] {
    const uniqueTests = new Map<string, Test>();
    
    tests.forEach(test => {
      const key = test._id || test.id || test.dimensions[0]?.area?.name;
      if (key && !uniqueTests.has(key)) {
        uniqueTests.set(key, test);
      }
    });
    
    return Array.from(uniqueTests.values());
  }

  /**
   * Inicializa los pasos de progreso
   */
  private initializeProgressSteps(): void {
    this.progressSteps = [
      { id: "prueba", title: "Prueba", completed: false },
      { id: "resultado", title: "Resultado", completed: false }
    ];
  }

  /**
   * Actualiza los porcentajes de los tests
   */
  updatePorcentTests(): void {
    this.testsCompleted = 0;
    this.dimensionsCompleted = 0;

    this.tests.forEach((test: Test) => {
      if (test.completed) this.testsCompleted++;
      
      let progress = 0;
      let total = 0;
      
      test.dimensions.forEach((dimension: TestDimension) => {
        if (dimension.completed) this.dimensionsCompleted++;
        
        dimension.questions.forEach((question: Question) => {
          total++;
          if (this.isQuestionAnswered(question)) {
            progress++;
          }
        });
      });
      
      test.progress = Math.round((progress / total) * 100);
      test.totalProgress = total;
    });

    // Actualizar el estado de progressSteps
    const pruebaStep = this.progressSteps.find(step => step.id === "prueba");
    if (pruebaStep) {
      pruebaStep.completed = this.testsCompleted === this.tests.length && this.tests.length > 0;
    }
  }

  /**
   * Verifica si una pregunta está respondida
   */
  private isQuestionAnswered(question: Question): boolean {
    if (!question.answer) {
      return false;
    }

    const valueNow = question.answer.valueNow;
    const valueFuture = question.answer.valueFuture;

    // Verificar que ambos valores existan y estén en el rango válido (1-10)
    // También aceptar 0 como válido si es necesario, pero normalmente debería ser 1-10
    const isValidNow = valueNow != null && valueNow >= 1 && valueNow <= 10;
    const isValidFuture = valueFuture != null && valueFuture >= 1 && valueFuture <= 10;

    return isValidNow && isValidFuture;
  }

  /**
   * Obtiene el número del test formateado
   */
  getTestNumber(test: Test): string {
    const testIndex = this.tests.findIndex(t => t === test);
    return String(testIndex + 1).padStart(2, '0');
  }

  /**
   * Obtiene el nombre del test
   */
  getTestName(test: Test): string {
    // Si el test tiene dimensiones, intentar obtener el nombre del área o dimensión
    if (test.dimensions && test.dimensions.length > 0) {
      const firstDimension = test.dimensions[0];
      // Priorizar el nombre del área si existe
      if (firstDimension.area && firstDimension.area.name) {
        return firstDimension.area.name;
      }
      // Si no hay área, usar el nombre de la dimensión
      if (firstDimension.name) {
        return firstDimension.name;
      }
    }
    // Fallback: retornar un nombre por defecto
    return 'Test sin nombre';
  }

  /**
   * TrackBy function para los tests
   */
  trackByTestId(index: number, test: Test): string {
    return test._id || test.id || index.toString();
  }

  /**
   * Inicia un test específico
   */
  startTest(test: Test): void {
    // Limpiar estado anterior
    this.selectedSubdimension = null;
    this.showResultsView = false;
    this.resultsData = null;
    this.completedSubdimension = null;
    this.currentStep = 1;
    this.clearStepperCache();
    
    // Establecer el nuevo test
    this.testSelected = test;
    this.subdimensions = this.calculateSubdimensions(test);
    this.testInProgress = true;
  }

  /**
   * Calcula las subdimensiones para un test específico
   */
  private calculateSubdimensions(test: Test): Subdimension[] {
    if (!test?.dimensions) {
      return [];
    }

    // Agrupar dimensiones por área
    const dimensionsByArea = new Map<string, TestDimension[]>();
    
    test.dimensions.forEach((dimension: TestDimension) => {
      const areaName = dimension.area?.name;
      if (areaName) {
        if (!dimensionsByArea.has(areaName)) {
          dimensionsByArea.set(areaName, []);
        }
        dimensionsByArea.get(areaName)!.push(dimension);
      }
    });

    // Crear subdimensiones basadas en las dimensiones agrupadas
    const subdimensions: Subdimension[] = [];
    
    dimensionsByArea.forEach((dimensions: TestDimension[], areaName: string) => {
      dimensions.forEach((dimension: TestDimension, index: number) => {
        const progress = this.calculateSubdimensionProgress(test, dimension.name);
        const readableName = this.formatDimensionName(dimension.name);
        
        subdimensions.push({
          name: readableName,
          originalName: dimension.name,
          areaName: areaName,
          progress: progress,
          id: `subdimension-${areaName}-${index}`,
          dimension: dimension
        });
      });
    });

    return subdimensions;
  }

  /**
   * Formatea el nombre de la dimensión para mostrarlo de forma más legible
   */
  private formatDimensionName(dimensionName: string): string {
    const nameMap: { [key: string]: string } = {
      'SALUD Y BIENESTAR': 'Salud y bienestar',
      'DIVERSIÓN': 'Diversión',
      'CRECIMIENTO PERSONAL': 'Crecimiento personal',
      'TRANSFORMACIÓN': 'Transformación',
      'CÍRCULOS SOCIALES': 'Círculos sociales',
      'FAMILIA': 'Familia',
      'TRABAJO Y DESARROLLO PROFESIONAL': 'Trabajo y desarrollo profesional',
      'INGRESOS ECONÓMICOS': 'Ingresos económicos'
    };

    return nameMap[dimensionName] || dimensionName;
  }

  /**
   * Calcula el progreso de una subdimensión específica
   */
  private calculateSubdimensionProgress(test: Test, dimensionName: string): number {
    if (!test?.dimensions) {
      return 0;
    }

    const dimension = test.dimensions.find((dim: TestDimension) => 
      dim.name === dimensionName
    );

    if (!dimension?.questions) {
      return 0;
    }

    let answeredQuestions = 0;
    const totalQuestions = dimension.questions.length;

    dimension.questions.forEach((question: Question) => {
      if (this.isQuestionAnswered(question)) {
        answeredQuestions++;
      }
    });

    if (totalQuestions === 0) {
      return 0;
    }

    // Calcular el porcentaje de forma más precisa
    const percentage = (answeredQuestions / totalQuestions) * 100;
    
    // Si todas las preguntas están respondidas, asegurar que sea 100%
    if (answeredQuestions === totalQuestions) {
      return 100;
    }
    
    // Usar Math.floor para evitar redondeos hacia arriba que puedan causar problemas
    // pero si está muy cerca de 100%, usar 100%
    if (percentage >= 99.5) {
      return 100;
    }
    
    return Math.floor(percentage);
  }

  /**
   * Obtiene las subdimensiones
   */
  getSubdimensions(): Subdimension[] {
    return this.subdimensions;
  }

  /**
   * Inicia una subdimensión específica
   */
  startSubdimension(subdimension: Subdimension): void {
    if (subdimension.progress === 100) {
      this.showResultsForSubdimension(subdimension);
      return;
    }
    
    const correspondingDimension = this.testSelected!.dimensions.find((dim: TestDimension) => 
      dim.name === subdimension.originalName
    );

    this.selectedSubdimension = {
      ...this.testSelected,
      subdimension: subdimension,
      dimensions: [{
        ...correspondingDimension,
        area: {
          ...this.testSelected!.dimensions[0].area,
          name: this.testSelected!.dimensions[0].area.name
        }
      }]
    };
    
    this.currentStep = 1;
    this.clearStepperCache();
    this.loadCurrentQuestionValues();
    this.updateCurrentQuestions();
  }

  /**
   * Limpia el caché del stepper
   */
  private clearStepperCache(): void {
    this.cachedStepperSteps = [];
    this.lastStepperCacheKey = '';
  }

  /**
   * Obtiene los pasos del stepper (con caché para evitar bucles infinitos)
   */
  getStepperSteps(): StepperStep[] {
    const cacheKey = `${this.selectedSubdimension?.subdimension?.originalName || 'none'}-${this.currentStep}`;
    
    if (this.cachedStepperSteps.length > 0 && this.lastStepperCacheKey === cacheKey) {
      return this.cachedStepperSteps;
    }
    
    const totalSteps = this.getTotalSteps();
    const steps: StepperStep[] = [];
    
    for (let i = 1; i <= totalSteps; i++) {
      steps.push({
        completed: i < this.currentStep,
        active: i === this.currentStep,
        pending: i > this.currentStep
      });
    }
    
    this.cachedStepperSteps = steps;
    this.lastStepperCacheKey = cacheKey;
    
    return steps;
  }

  /**
   * Obtiene el número total de steps para la dimensión actual
   */
  private getTotalSteps(): number {
    const currentDimension = this.findCurrentDimension();
    return currentDimension?.questions?.length || 5;
  }

  /**
   * Busca la dimensión actual en las fuentes disponibles
   */
  private findCurrentDimension(): TestDimension | null {
    if (!this.testSelected || !this.selectedSubdimension) {
      return null;
    }

    // Primero buscar en testSelected.dimensions
    let currentDimension = this.testSelected.dimensions.find((dim: TestDimension) => 
      dim.name === this.selectedSubdimension.subdimension.originalName
    );
    
    // Si no se encuentra, buscar en selectedSubdimension.dimensions
    if (!currentDimension && this.selectedSubdimension.dimensions) {
      currentDimension = this.selectedSubdimension.dimensions.find((dim: TestDimension) => 
        dim.name === this.selectedSubdimension.subdimension.originalName
      );
    }

    return currentDimension || null;
  }

  /**
   * Obtiene el título de la sección actual
   */
  getCurrentSectionTitle(): string {
    const currentDimension = this.findCurrentDimension();
    if (!currentDimension?.questions) {
      return 'Sección';
    }

    const currentQuestion = currentDimension.questions[this.currentStep - 1];
    return currentQuestion?.name || 'Sección';
  }

  /**
   * Actualiza las preguntas de la sección actual
   */
  updateCurrentQuestions(): void {
    const currentDimension = this.findCurrentDimension();
    
    if (!currentDimension?.questions) {
      this.currentQuestions = [];
      return;
    }

    const currentQuestion = currentDimension.questions[this.currentStep - 1];
    if (!currentQuestion) {
      this.currentQuestions = [];
      return;
    }

    const individualQuestions = this.parseQuestionsFromTitle(currentQuestion.title);
    
    this.currentQuestions = individualQuestions.map((questionText: string, index: number) => ({
      name: currentQuestion.name,
      text: questionText.trim(),
      score: 5,
      originalIndex: index + 1
    }));
  }

  /**
   * Parsea las preguntas individuales del título
   */
  private parseQuestionsFromTitle(questionsText: string): string[] {
    // Primero intentar dividir por saltos de línea
    let individualQuestions = questionsText.split('\n').filter((q: string) => q.trim().length > 0);
    
    // Si no hay saltos de línea o solo hay una pregunta, dividir por patrones de preguntas
    if (individualQuestions.length <= 1) {
      const questionPattern = /¿[^¿]*?[?]/g;
      const matches = questionsText.match(questionPattern);
      
      if (matches && matches.length > 0) {
        individualQuestions = matches.map((match: string) => match.trim());
      } else {
        individualQuestions = [questionsText.trim()];
      }
    }
    
    return individualQuestions;
  }

  /**
   * Carga los valores existentes del step actual
   */
  loadCurrentQuestionValues(): void {
    const currentDimension = this.findCurrentDimension();
    
    if (!currentDimension?.questions) {
      this.currentSatisfaction = 1;
      this.futureSatisfaction = 1;
      return;
    }

    const currentQuestion = currentDimension.questions[this.currentStep - 1];
    
    if (this.isQuestionAnswered(currentQuestion)) {
      this.currentSatisfaction = currentQuestion.answer!.valueNow;
      this.futureSatisfaction = currentQuestion.answer!.valueFuture;
    } else {
      this.currentSatisfaction = 1;
      this.futureSatisfaction = 1;
    }
  }

  /**
   * Navega a un step específico
   */
  goToStep(stepNumber: number): void {
    const totalSteps = this.getTotalSteps();
    
    if (stepNumber < 1 || stepNumber > totalSteps) {
      console.warn('Step fuera de rango:', stepNumber, 'Rango válido: 1 -', totalSteps);
      return;
    }
    
    const steps = this.getStepperSteps();
    const targetStep = steps[stepNumber - 1];
    
    if (!targetStep || (!targetStep.completed && !targetStep.active)) {
      console.warn('No se puede navegar al step:', stepNumber, 'Estado:', targetStep);
      return;
    }

    this.currentStep = stepNumber;
    this.clearStepperCache();
    this.loadCurrentQuestionValues();
    this.updateCurrentQuestions();
  }

  /**
   * Pasa al siguiente paso
   */
  nextStep(): void {
    if (this.isSaving) {
      return;
    }
    
    // Validar que currentSatisfaction sea menor que futureSatisfaction
    if (this.currentSatisfaction > this.futureSatisfaction) {
      this.error('La satisfacción actual debe ser menor que la satisfacción futura. Por favor, ajusta los valores correctamente antes de continuar.');
      return;
    }
    
    this.saveCurrentQuestionAndContinue();
  }

  /**
   * Guarda la pregunta actual y continúa con la navegación
   */
  private saveCurrentQuestionAndContinue(): void {
    if (!this.selectedSubdimension || !this.testSelected) {
      return;
    }
    
    this.isSaving = true;
    this.saveError = null;
    
    const currentDimension = this.findCurrentDimension();
    if (!currentDimension?.questions) {
      this.isSaving = false;
      return;
    }

    const currentQuestion = currentDimension.questions[this.currentStep - 1];
    if (!currentQuestion) {
      this.isSaving = false;
      return;
    }
    
    const testKnowRequest: TestKnowRequest = {
      testId: this.selectedSubdimension._id || this.selectedSubdimension.id || this.testSelected._id || this.testSelected.id!,
      dimensionName: this.selectedSubdimension.subdimension.originalName,
      questionName: currentQuestion.name,
      questionAnswer: {
        valueNow: this.currentSatisfaction,
        valueFuture: this.futureSatisfaction
      },
      priority: this.currentStep
    };

    this.testService.updateTestKnow(testKnowRequest)
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          this.reloadTestDataAndContinue();
        },
        error: (error) => {
          this.isSaving = false;
          this.saveError = this.getErrorMessage(error);
        }
      });
  }

  /**
   * Obtiene el mensaje de error apropiado
   */
  private getErrorMessage(error: any): string {
    if (error.error?.message) {
      return error.error.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'Error al guardar la pregunta. Por favor, inténtalo de nuevo.';
  }

  /**
   * Recarga los datos del test y continúa con la navegación
   */
  private reloadTestDataAndContinue(): void {
    if (!this.authUser?.id) {
      console.error('AuthUser.id no disponible en reloadTestDataAndContinue');
      return;
    }
    
    const totalSteps = this.getTotalSteps();
    const isLastStep = this.currentStep >= totalSteps;
    
    this.testService.find(this.authUser.id, TEST_TYPES.Autoconocimiento.id)
      .subscribe({
        next: (res: any) => {
          if (res.data) {
            this.tests = this.initializeTests(res.data);
            this.updatePorcentTests();
            this.updateSelectedTest();
          }
          
          // Si es el último step, no continuar navegación, solo mostrar resultados
          if (isLastStep) {
            this.handleLastStepCompleted();
          } else {
            this.continueNavigation();
          }
        },
        error: (err: any) => {
          console.error('Error al recargar datos del test:', err);
          // Si es el último step, intentar mostrar resultados de todos modos
          if (isLastStep) {
            this.handleLastStepCompleted();
          } else {
            this.continueNavigation();
          }
        }
      });
  }

  /**
   * Actualiza el test seleccionado con los datos más recientes
   */
  private updateSelectedTest(): void {
    if (this.testSelected) {
      const updatedTest = this.tests.find(t => 
        (t._id && this.testSelected!._id && t._id === this.testSelected!._id) || 
        (t.id && this.testSelected!.id && t.id === this.testSelected!.id) ||
        (t.dimensions && t.dimensions[0]?.area?.name === this.testSelected!.dimensions[0]?.area?.name)
      );
      if (updatedTest) {
        this.testSelected = updatedTest;
        // Recalcular subdimensiones con los datos actualizados
        this.subdimensions = this.calculateSubdimensions(this.testSelected);
        
        if (this.selectedSubdimension) {
          // Buscar la subdimensión actualizada por originalName en lugar de id
          // ya que el id podría no coincidir después de recalcular
          const updatedSubdimension = this.subdimensions.find(sub => 
            sub.originalName === this.selectedSubdimension.subdimension.originalName ||
            sub.id === this.selectedSubdimension.subdimension.id
          );
          if (updatedSubdimension) {
            // Buscar la dimensión actualizada con las preguntas que tienen respuestas
            let dimensionToUse = this.testSelected.dimensions.find((dim: TestDimension) => 
              dim.name === updatedSubdimension.originalName
            );
            
            // Si no se encuentra por originalName, buscar por área
            if (!dimensionToUse) {
              dimensionToUse = this.testSelected.dimensions.find((dim: TestDimension) => 
                dim.area?.name === updatedSubdimension.areaName
              );
            }
            
            if (dimensionToUse && dimensionToUse.questions && dimensionToUse.questions.length > 0) {
              // Actualizar selectedSubdimension con la dimensión actualizada que tiene las respuestas
              this.selectedSubdimension = {
                ...this.testSelected,
                subdimension: updatedSubdimension,
                dimensions: [dimensionToUse]
              };
              console.log('updateSelectedTest: selectedSubdimension actualizado con dimensión que tiene respuestas', {
                dimensionName: dimensionToUse.name,
                questionsCount: dimensionToUse.questions.length,
                questionsWithAnswers: dimensionToUse.questions.filter((q: Question) => q.answer).length
              });
            } else {
              // Si no se encuentra, solo actualizar la subdimensión
              this.selectedSubdimension.subdimension = updatedSubdimension;
              console.warn('updateSelectedTest: No se encontró dimensión con preguntas, solo actualizando subdimensión');
            }
          }
        }
      }
    }
  }

  /**
   * Continúa con la navegación después de guardar
   */
  private continueNavigation(): void {
    const totalSteps = this.getTotalSteps();
    
    if (this.currentStep < totalSteps) {
      this.currentStep++;
      this.clearStepperCache();
      this.loadCurrentQuestionValues();
      this.updateCurrentQuestions();
    } else {
      this.handleLastStepCompleted();
    }
  }

  /**
   * Maneja cuando se completa el último step
   * Siempre finaliza y muestra la ficha de resultado de la subdimensión
   */
  private handleLastStepCompleted(): void {
    // Siempre finalizar y mostrar la ficha de resultado
    this.completeAllSubdimensions();
  }

  /**
   * Completa todas las subdimensiones y muestra la ficha de resultado
   */
  private completeAllSubdimensions(): void {
    // Guardar información de la subdimensión antes de limpiar
    const subdimensionOriginalName = this.selectedSubdimension?.subdimension?.originalName;
    const subdimensionId = this.selectedSubdimension?.subdimension?.id;
    
    if (!this.selectedSubdimension?.subdimension || !this.testSelected) {
      console.error('No hay subdimensión o test seleccionado para mostrar resultados');
      return;
    }
    
    // Establecer estado de completado para mostrar mensaje de carga
    this.isCompletingSubdimension = true;
    this.currentStep = 1;
    // NO establecer testInProgress = false aquí, mantenerlo en true hasta que los resultados estén listos
    
    // Recargar datos primero para tener los datos actualizados
    // Pasar tanto el ID como el originalName para asegurar que se encuentre
    this.reloadTestDataAndShowResults(subdimensionId, subdimensionOriginalName);
  }
  
  /**
   * Recarga los datos del test y muestra los resultados
   */
  private reloadTestDataAndShowResults(subdimensionId?: string, subdimensionOriginalName?: string): void {
    if (!this.authUser?.id) {
      console.error('AuthUser.id no disponible en reloadTestDataAndShowResults');
      this.isCompletingSubdimension = false;
      return;
    }
    
    // Reducir el delay a 500ms o eliminarlo completamente si el servidor responde rápido
    // El delay mínimo es para asegurar que el servidor haya procesado el guardado
    setTimeout(() => {
      if (!this.authUser?.id) {
        console.error('AuthUser.id no disponible después del delay');
        this.isCompletingSubdimension = false;
        return;
      }
      
      console.log('reloadTestDataAndShowResults: Recargando datos del servidor...');
      this.testService.find(this.authUser.id, TEST_TYPES.Autoconocimiento.id)
        .subscribe({
          next: (res: any) => {
            if (res.data) {
              this.tests = this.initializeTests(res.data);
              this.updatePorcentTests();
              
              // Actualizar testSelected con los datos más recientes
              if (this.testSelected) {
                const updatedTest = this.tests.find(t => t._id === this.testSelected!._id || t.id === this.testSelected!.id);
                if (updatedTest) {
                  this.testSelected = updatedTest;
                  this.subdimensions = this.calculateSubdimensions(this.testSelected);
                  
                  // Actualizar selectedSubdimension con los datos actualizados
                  // Usar el originalName pasado como parámetro o el de completedSubdimension
                  const originalNameToUse = subdimensionOriginalName || this.completedSubdimension?.subdimension?.originalName;
                  
                  if (originalNameToUse) {
                    // Buscar la subdimensión actualizada por originalName (más confiable que ID)
                    let updatedSubdimension = this.subdimensions.find(sub => 
                      sub.originalName === originalNameToUse
                    );
                    
                    // Si no se encuentra por originalName, buscar por ID
                    if (!updatedSubdimension && subdimensionId) {
                      updatedSubdimension = this.subdimensions.find(sub => sub.id === subdimensionId);
                    }
                    
                    if (updatedSubdimension) {
                      // Buscar la dimensión correspondiente en testSelected con las preguntas actualizadas
                      // Priorizar búsqueda por originalName
                      let dimensionToUse = this.testSelected.dimensions.find((dim: TestDimension) => 
                        dim.name === updatedSubdimension.originalName
                      );
                      
                      // Si no se encuentra por originalName, buscar por área
                      if (!dimensionToUse) {
                        dimensionToUse = this.testSelected.dimensions.find((dim: TestDimension) => 
                          dim.area?.name === updatedSubdimension.areaName
                        );
                      }
                      
                      if (dimensionToUse && dimensionToUse.questions && dimensionToUse.questions.length > 0) {
                        // Verificar que las preguntas tengan respuestas
                        const questionsWithAnswers = dimensionToUse.questions.filter((q: Question) => q.answer);
                        console.log('reloadTestDataAndShowResults: Dimensión encontrada con respuestas', {
                          dimensionName: dimensionToUse.name,
                          totalQuestions: dimensionToUse.questions.length,
                          questionsWithAnswers: questionsWithAnswers.length,
                          sampleQuestion: dimensionToUse.questions[0] ? {
                            name: dimensionToUse.questions[0].name,
                            hasAnswer: !!dimensionToUse.questions[0].answer,
                            valueNow: dimensionToUse.questions[0].answer?.valueNow,
                            valueFuture: dimensionToUse.questions[0].answer?.valueFuture
                          } : null
                        });
                        
                        this.selectedSubdimension = {
                          ...this.testSelected,
                          subdimension: updatedSubdimension,
                          dimensions: [dimensionToUse]
                        };
                        this.completedSubdimension = this.selectedSubdimension;
                        console.log('reloadTestDataAndShowResults: completedSubdimension establecido correctamente', {
                          subdimensionName: updatedSubdimension.name,
                          originalName: updatedSubdimension.originalName,
                          dimensionsCount: this.selectedSubdimension.dimensions.length
                        });
                      } else {
                        console.warn('No se encontró la dimensión con preguntas, buscando alternativa...', {
                          updatedSubdimension,
                          originalName: updatedSubdimension.originalName,
                          areaName: updatedSubdimension.areaName,
                          availableDimensions: this.testSelected.dimensions.map((d: TestDimension) => ({
                            name: d.name,
                            areaName: d.area?.name,
                            questionsCount: d.questions?.length || 0,
                            questionsWithAnswers: d.questions?.filter((q: Question) => q.answer).length || 0
                          }))
                        });
                        // Si no se encuentra, usar la primera dimensión del test que tenga preguntas con respuestas
                        const dimensionWithAnswers = this.testSelected.dimensions.find((dim: TestDimension) => 
                          dim.questions && dim.questions.length > 0 && 
                          dim.questions.some((q: Question) => q.answer)
                        );
                        
                        if (dimensionWithAnswers) {
                          this.selectedSubdimension = {
                            ...this.testSelected,
                            subdimension: updatedSubdimension,
                            dimensions: [dimensionWithAnswers]
                          };
                          this.completedSubdimension = this.selectedSubdimension;
                          console.log('reloadTestDataAndShowResults: completedSubdimension establecido con dimensionWithAnswers', {
                            subdimensionName: updatedSubdimension.name,
                            originalName: updatedSubdimension.originalName
                          });
                        }
                      }
                    }
                  }
                }
              }
              
              // Mostrar la ficha de resultado después de actualizar los datos
              // Intentar usar completedSubdimension primero, luego selectedSubdimension, y finalmente buscar por originalName
              let subdimensionToShow: Subdimension | null = null;
              
              if (this.completedSubdimension?.subdimension) {
                subdimensionToShow = this.completedSubdimension.subdimension;
                console.log('reloadTestDataAndShowResults: Usando completedSubdimension.subdimension para mostrar resultados');
              } else if (this.selectedSubdimension?.subdimension) {
                subdimensionToShow = this.selectedSubdimension.subdimension;
                console.log('reloadTestDataAndShowResults: Usando selectedSubdimension.subdimension para mostrar resultados');
              } else if (subdimensionOriginalName) {
                // Buscar la subdimensión por originalName en subdimensions
                const foundSubdimension = this.subdimensions.find(sub => sub.originalName === subdimensionOriginalName);
                if (foundSubdimension) {
                  subdimensionToShow = foundSubdimension;
                  console.log('reloadTestDataAndShowResults: Encontrada subdimensión por originalName para mostrar resultados');
                }
              }
              
              if (subdimensionToShow) {
                console.log('reloadTestDataAndShowResults: Llamando a showResultsForSubdimension', {
                  subdimensionName: subdimensionToShow.name,
                  originalName: subdimensionToShow.originalName
                });
                // Cambiar estados antes de mostrar resultados
                this.isCompletingSubdimension = false;
                this.testInProgress = false;
                this.showResultsForSubdimension(subdimensionToShow);
              } else {
                console.error('reloadTestDataAndShowResults: No se pudo encontrar la subdimensión para mostrar resultados', {
                  completedSubdimension: this.completedSubdimension,
                  selectedSubdimension: this.selectedSubdimension,
                  subdimensionOriginalName,
                  subdimensions: this.subdimensions.map(sub => ({
                    name: sub.name,
                    originalName: sub.originalName
                  }))
                });
                this.isCompletingSubdimension = false;
              }
              
              // Verificar si todos los tests están completados
              if (this.areAllDimensionsCompleted()) {
                this.showCompletionModal = true;
              }
            }
          },
          error: (err: any) => {
            console.error('Error al recargar datos del test:', err);
            this.isCompletingSubdimension = false;
            // Intentar mostrar resultados con los datos actuales si hay error
            if (this.completedSubdimension?.subdimension) {
              this.testInProgress = false;
              this.showResultsForSubdimension(this.completedSubdimension.subdimension);
            }
          }
        });
    }, 500); // Reducido a 500ms para una respuesta más rápida
  }

  /**
   * Recarga los datos del test y verifica si todos están completados
   */
  private reloadTestDataAndCheckCompletion(): void {
    if (!this.authUser?.id) {
      console.error('AuthUser.id no disponible en reloadTestDataAndCheckCompletion');
      return;
    }
    
    this.testService.find(this.authUser.id, TEST_TYPES.Autoconocimiento.id)
      .subscribe({
        next: (res: any) => {
          if (res.data) {
            this.tests = this.initializeTests(res.data);
            this.updatePorcentTests();
            if (this.testSelected) {
              const updatedTest = this.tests.find(t => t._id === this.testSelected!._id || t.id === this.testSelected!.id);
              if (updatedTest) {
                this.testSelected = updatedTest;
                this.subdimensions = this.calculateSubdimensions(this.testSelected);
              }
            }
            
            // Verificar si todos los tests están completados antes de mostrar el modal
            if (this.areAllDimensionsCompleted()) {
              this.generateResultsData();
              // Mostrar modal de finalización solo cuando todas las dimensiones están al 100%
              this.showCompletionModal = true;
            }
          }
        },
        error: (err: any) => {
          console.error('Error al recargar datos del test:', err);
        }
      });
  }

  /**
   * Recarga los datos del test
   */
  private reloadTestData(): void {
    if (!this.authUser?.id) {
      console.error('AuthUser.id no disponible en reloadTestData');
      return;
    }
    
    this.testService.find(this.authUser.id, TEST_TYPES.Autoconocimiento.id)
      .subscribe({
        next: (res: any) => {
          if (res.data) {
            this.tests = this.initializeTests(res.data);
            this.updatePorcentTests();
            if (this.testSelected) {
              const updatedTest = this.tests.find(t => t._id === this.testSelected!._id || t.id === this.testSelected!.id);
              if (updatedTest) {
                this.testSelected = updatedTest;
                this.subdimensions = this.calculateSubdimensions(this.testSelected);
              }
            }
          }
        },
        error: (err: any) => {
          console.error('Error al recargar datos del test:', err);
        }
      });
  }

  /**
   * Obtiene el texto del botón siguiente
   */
  getNextButtonText(): string {
    if (this.isSaving) {
      return 'Guardando...';
    }
    
    const totalSteps = this.getTotalSteps();
    
    if (this.currentStep < totalSteps) {
      return 'Siguiente';
    } else {
      // Siempre finalizar y mostrar la ficha de resultado
      return 'Finalizar';
    }
  }

  /**
   * Muestra la vista de resultados para una subdimensión específica
   */
  showResultsForSubdimension(subdimension: Subdimension): void {
    console.log('showResultsForSubdimension: Iniciando', {
      subdimensionName: subdimension.name,
      originalName: subdimension.originalName,
      hasSelectedSubdimension: !!this.selectedSubdimension,
      hasDimensions: !!(this.selectedSubdimension?.dimensions && this.selectedSubdimension.dimensions.length > 0)
    });
    
    // Si ya tenemos selectedSubdimension actualizado con los datos del servidor, no sobrescribirlo
    if (!this.selectedSubdimension || !this.selectedSubdimension.dimensions || this.selectedSubdimension.dimensions.length === 0) {
      console.log('showResultsForSubdimension: selectedSubdimension no tiene dimensions, buscando en testSelected');
      // Buscar la dimensión correspondiente en testSelected
      const dimensionToUse = this.testSelected?.dimensions.find((dim: TestDimension) => 
        dim.name === subdimension.originalName || 
        dim.area?.name === subdimension.name ||
        dim.area?.name === subdimension.areaName
      );
      
      if (dimensionToUse) {
        console.log('showResultsForSubdimension: Dimensión encontrada en testSelected', {
          dimensionName: dimensionToUse.name,
          questionsCount: dimensionToUse.questions?.length || 0
        });
        this.selectedSubdimension = {
          ...this.testSelected,
          subdimension: subdimension,
          dimensions: [dimensionToUse]
        };
      } else {
        console.warn('showResultsForSubdimension: No se encontró dimensión, usando fallback');
        // Fallback: usar la primera dimensión si no se encuentra
        if (this.testSelected && this.testSelected.dimensions && this.testSelected.dimensions.length > 0) {
          this.selectedSubdimension = {
            ...this.testSelected,
            subdimension: subdimension,
            dimensions: [{
              ...this.testSelected.dimensions[0],
              area: {
                ...this.testSelected.dimensions[0].area,
                name: subdimension.name
              }
            }]
          };
        }
      }
    } else {
      console.log('showResultsForSubdimension: Usando selectedSubdimension existente con dimensions');
    }
    
    // Asegurar que completedSubdimension esté establecido
    if (!this.completedSubdimension && this.selectedSubdimension) {
      this.completedSubdimension = this.selectedSubdimension;
    }
    
    console.log('showResultsForSubdimension: Generando datos de resultados');
    this.generateResultsData();
    
    console.log('showResultsForSubdimension: Mostrando vista de resultados', {
      testInProgress: this.testInProgress,
      showResultsView: this.showResultsView,
      hasResultsData: !!this.resultsData
    });
    
    this.testInProgress = false; // Ocultar testInProgress cuando se muestran los resultados
    this.showResultsView = true;
    this.completedSubdimension = this.selectedSubdimension;
    
    console.log('showResultsForSubdimension: Completado', {
      testInProgress: this.testInProgress,
      showResultsView: this.showResultsView,
      hasResultsData: !!this.resultsData
    });
  }

  /**
   * Genera los datos de resultados para la vista
   */
  private generateResultsData(): void {
    const subdimensionToUse = this.completedSubdimension || this.selectedSubdimension;
    
    if (!subdimensionToUse || !this.testSelected) {
      console.error('generateResultsData: No hay subdimensión o test seleccionado', {
        completedSubdimension: this.completedSubdimension,
        selectedSubdimension: this.selectedSubdimension,
        testSelected: this.testSelected
      });
      return;
    }

    // Asegurar que selectedSubdimension esté disponible
    if (!this.selectedSubdimension && this.completedSubdimension) {
      this.selectedSubdimension = this.completedSubdimension;
    }

    // PRIORIDAD 1: Usar selectedSubdimension.dimensions[0] que ya tiene los datos actualizados
    let dimension: TestDimension | null = null;
    
    if (this.selectedSubdimension?.dimensions && this.selectedSubdimension.dimensions.length > 0) {
      dimension = this.selectedSubdimension.dimensions[0];
      if (dimension) {
        console.log('generateResultsData: Usando dimension de selectedSubdimension', {
          dimensionName: dimension.name,
          questionsCount: dimension.questions?.length || 0,
          questionsWithAnswers: dimension.questions?.filter((q: Question) => q.answer).length || 0
        });
      }
    }
    
    // PRIORIDAD 2: Si no está en selectedSubdimension, buscar en testSelected usando originalName
    if (!dimension && this.selectedSubdimension?.subdimension?.originalName) {
      dimension = this.testSelected.dimensions.find((dim: TestDimension) => 
        dim.name === this.selectedSubdimension.subdimension.originalName
      ) || null;
      console.log('generateResultsData: Buscando en testSelected por originalName', {
        originalName: this.selectedSubdimension.subdimension.originalName,
        found: !!dimension
      });
    }
    
    // PRIORIDAD 3: Usar findCurrentDimension como fallback
    if (!dimension) {
      dimension = this.findCurrentDimension();
      console.log('generateResultsData: Usando findCurrentDimension como fallback', {
        found: !!dimension
      });
    }

    if (!dimension?.questions || dimension.questions.length === 0) {
      console.error('generateResultsData: No se encontró la dimensión o no tiene preguntas', {
        dimension,
        testSelected: this.testSelected,
        selectedSubdimension: this.selectedSubdimension,
        subdimensionOriginalName: this.selectedSubdimension?.subdimension?.originalName,
        testSelectedDimensions: this.testSelected.dimensions.map((d: TestDimension) => d.name),
        selectedSubdimensionDimensions: this.selectedSubdimension?.dimensions?.map((d: TestDimension) => d.name)
      });
      return;
    }

    // Verificar que las preguntas tengan respuestas
    if (!dimension) {
      console.error('generateResultsData: dimension es null');
      return;
    }

    const questionsWithAnswers = dimension.questions.filter((q: Question) => q.answer);
    console.log('generateResultsData: Preguntas con respuestas', {
      totalQuestions: dimension.questions.length,
      questionsWithAnswers: questionsWithAnswers.length,
      questions: dimension.questions.map((q: Question) => ({
        name: q.name,
        hasAnswer: !!q.answer,
        valueNow: q.answer?.valueNow,
        valueFuture: q.answer?.valueFuture
      }))
    });

    const tableData = dimension.questions.map((question: Question) => {
      const presentValue = question.answer?.valueNow || 0;
      const futureValue = question.answer?.valueFuture || 0;
      const gap = futureValue - presentValue;
      
      return {
        area: question.name,
        presentAnalysis: presentValue,
        futureAnalysis: futureValue,
        gapToClose: gap,
        gapLevel: this.getGapLevel(gap)
      };
    });

    const averages = this.calculateAverages(tableData);
    const recommendedReadings = this.generateRecommendedReadings(tableData);

    this.resultsData = {
      subdimensionName: subdimensionToUse.subdimension.name,
      tableData: tableData,
      averages: averages,
      recommendedReadings: recommendedReadings
    };
    console.log('generateResultsData: resultsData generado', this.resultsData);
  }

  /**
   * Calcula los promedios de la tabla de datos
   */
  private calculateAverages(tableData: any[]): { presentAnalysis: number; futureAnalysis: number; gapToClose: number } {
    const totalPresent = tableData.reduce((sum: number, item: any) => sum + item.presentAnalysis, 0);
    const totalFuture = tableData.reduce((sum: number, item: any) => sum + item.futureAnalysis, 0);
    const totalGap = tableData.reduce((sum: number, item: any) => sum + item.gapToClose, 0);
    const count = tableData.length;

    return {
      presentAnalysis: Math.round((totalPresent / count) * 10) / 10,
      futureAnalysis: Math.round((totalFuture / count) * 10) / 10,
      gapToClose: Math.round((totalGap / count) * 10) / 10
    };
  }

  /**
   * Genera las lecturas recomendadas
   */
  private generateRecommendedReadings(tableData: any[]): any[] {
    const topGaps = tableData
      .sort((a: any, b: any) => b.gapToClose - a.gapToClose)
      .slice(0, 3);

    return topGaps.map((item: any) => {
      const gapLevel = this.getGapLevel(item.gapToClose);
      const areaName = item.area;
      const gapRange = this.getGapRange(item.gapToClose);
      
      let description = '';
      if (environment.gapDescriptions && environment.gapDescriptions[areaName as keyof typeof environment.gapDescriptions]) {
        const areaDescriptions = environment.gapDescriptions[areaName as keyof typeof environment.gapDescriptions];
        if (areaDescriptions[gapRange as keyof typeof areaDescriptions]) {
          description = areaDescriptions[gapRange as keyof typeof areaDescriptions];
        } else {
          description = this.getFallbackDescription(gapLevel, item.gapToClose);
        }
      } else {
        description = this.getFallbackDescription(gapLevel, item.gapToClose);
      }

      return {
        title: item.area,
        description: description,
        link: '/dashboard/academy',
        linkText: 'Academy',
        gapValue: item.gapToClose,
        gapLevel: gapLevel
      };
    });
  }

  /**
   * Determina el nivel de brecha basado en el valor
   */
  getGapLevel(gap: number): string {
    if (gap >= 1 && gap <= 3) return 'low';
    if (gap >= 4 && gap <= 6) return 'medium';
    if (gap >= 7 && gap <= 9) return 'high';
    return 'low';
  }

  /**
   * Obtiene el rango de brecha
   */
  getGapRange(gap: number): string {
    if (gap >= 1 && gap <= 3) return '1-3';
    if (gap >= 4 && gap <= 6) return '4-6';
    if (gap >= 7 && gap <= 9) return '7-9';
    return '1-3';
  }

  /**
   * Obtiene una descripción de respaldo
   */
  private getFallbackDescription(gapLevel: string, gapValue: number): string {
    const gapRange = this.getGapRange(gapValue);
    const defaultArea = 'Salud y Bienestar';
    
    if (environment.gapDescriptions && environment.gapDescriptions[defaultArea as keyof typeof environment.gapDescriptions]) {
      const defaultDescriptions = environment.gapDescriptions[defaultArea as keyof typeof environment.gapDescriptions];
      if (defaultDescriptions[gapRange as keyof typeof defaultDescriptions]) {
        return defaultDescriptions[gapRange as keyof typeof defaultDescriptions];
      }
    }
    
    if (gapLevel === 'high') {
      return `Oportunidad crítica que requiere acción inmediata. Brecha alta (${gapValue} puntos). Fortalece esta área con recursos de Academy.`;
    } else if (gapLevel === 'medium') {
      return `Brecha media (${gapValue} puntos). Mejora esta área con consejos especializados en Academy.`;
    } else {
      return `Brecha baja (${gapValue} puntos). Mantén y fortalece esta área con recursos de Academy.`;
    }
  }

  /**
   * Cierra la vista de resultados
   */
  closeResultsView(): void {
    this.showResultsView = false;
    this.resultsData = null;
    this.completedSubdimension = null;
    this.selectedSubdimension = null;
  }

  /**
   * Finaliza el test desde la vista de resultados o navega a la siguiente subdimensión
   */
  finishTestFromResults(): void {
    // Buscar la siguiente subdimensión pendiente en TODOS los tests
    const nextResult = this.getNextPendingSubdimensionAcrossAllTests();
    
    if (nextResult) {
      // Cerrar la vista de resultados primero
      this.showResultsView = false;
      this.resultsData = null;
      this.completedSubdimension = null;
      
      // Asegurar que el test esté seleccionado
      this.testSelected = nextResult.test;
      this.subdimensions = this.calculateSubdimensions(nextResult.test);
      
      // Asegurar que testInProgress esté en true para mostrar la vista de preguntas
      this.testInProgress = true;
      
      // Iniciar la subdimensión (esto configurará selectedSubdimension y mostrará las preguntas)
      this.startSubdimension(nextResult.subdimension);
    } else {
      // No hay más subdimensiones pendientes en ningún test, cerrar y volver al inicio
      this.closeResultsView();
      this.testInProgress = false;
      this.testSelected = null;
    }
  }

  /**
   * Obtiene el texto del botón de finalizar según el estado del test
   */
  getFinishButtonText(): string {
    // Verificar si hay más subdimensiones pendientes en TODOS los tests
    const nextSubdimension = this.getNextPendingSubdimensionAcrossAllTests();
    
    if (nextSubdimension) {
      return 'Siguiente subdimensión →';
    }
    return 'Finalizar →';
  }

  /**
   * Obtiene la siguiente subdimensión pendiente (que no esté al 100%) en el test actual
   */
  private getNextPendingSubdimension(): Subdimension | null {
    if (!this.testSelected || !this.subdimensions || this.subdimensions.length === 0) {
      return null;
    }

    // Obtener la subdimensión actual
    const currentSubdimension = this.completedSubdimension?.subdimension || 
                                this.selectedSubdimension?.subdimension;
    
    if (!currentSubdimension) {
      // Si no hay subdimensión actual, retornar la primera pendiente
      return this.subdimensions.find(sub => sub.progress < 100) || null;
    }

    // Encontrar el índice de la subdimensión actual
    const currentIndex = this.subdimensions.findIndex(
      sub => sub.id === currentSubdimension.id || 
             sub.originalName === currentSubdimension.originalName
    );

    if (currentIndex === -1) {
      // Si no se encuentra, retornar la primera pendiente
      return this.subdimensions.find(sub => sub.progress < 100) || null;
    }

    // Buscar la siguiente subdimensión pendiente después de la actual
    for (let i = currentIndex + 1; i < this.subdimensions.length; i++) {
      if (this.subdimensions[i].progress < 100) {
        return this.subdimensions[i];
      }
    }

    // Si no hay más después de la actual, buscar desde el inicio
    for (let i = 0; i < currentIndex; i++) {
      if (this.subdimensions[i].progress < 100) {
        return this.subdimensions[i];
      }
    }

    return null;
  }

  /**
   * Obtiene la siguiente subdimensión pendiente en TODOS los tests
   */
  private getNextPendingSubdimensionAcrossAllTests(): { subdimension: Subdimension; test: Test } | null {
    if (!this.tests || this.tests.length === 0) {
      return null;
    }

    // Obtener la subdimensión actual
    const currentSubdimension = this.completedSubdimension?.subdimension || 
                                this.selectedSubdimension?.subdimension;
    const currentTest = this.testSelected;

    // Si no hay subdimensión actual, buscar la primera pendiente en todos los tests
    if (!currentSubdimension || !currentTest) {
      for (const test of this.tests) {
        const subdimensions = this.calculateSubdimensions(test);
        const firstPending = subdimensions.find(sub => sub.progress < 100);
        if (firstPending) {
          return { subdimension: firstPending, test };
        }
      }
      return null;
    }

    // Encontrar el test actual y su índice
    const currentTestIndex = this.tests.findIndex(
      t => t._id === currentTest._id || t.id === currentTest.id
    );

    if (currentTestIndex === -1) {
      // Si no se encuentra el test actual, buscar desde el inicio
      for (const test of this.tests) {
        const subdimensions = this.calculateSubdimensions(test);
        const firstPending = subdimensions.find(sub => sub.progress < 100);
        if (firstPending) {
          return { subdimension: firstPending, test };
        }
      }
      return null;
    }

    // Buscar en el test actual después de la subdimensión actual
    const currentSubdimensions = this.calculateSubdimensions(currentTest);
    const currentSubdimensionIndex = currentSubdimensions.findIndex(
      sub => sub.id === currentSubdimension.id || 
             sub.originalName === currentSubdimension.originalName
    );

    // Buscar después de la subdimensión actual en el test actual
    if (currentSubdimensionIndex !== -1) {
      for (let i = currentSubdimensionIndex + 1; i < currentSubdimensions.length; i++) {
        if (currentSubdimensions[i].progress < 100) {
          return { subdimension: currentSubdimensions[i], test: currentTest };
        }
      }
    }

    // Buscar en los tests siguientes
    for (let testIndex = currentTestIndex + 1; testIndex < this.tests.length; testIndex++) {
      const test = this.tests[testIndex];
      const subdimensions = this.calculateSubdimensions(test);
      const firstPending = subdimensions.find(sub => sub.progress < 100);
      if (firstPending) {
        return { subdimension: firstPending, test };
      }
    }

    // Buscar desde el inicio hasta el test actual
    for (let testIndex = 0; testIndex < currentTestIndex; testIndex++) {
      const test = this.tests[testIndex];
      const subdimensions = this.calculateSubdimensions(test);
      const firstPending = subdimensions.find(sub => sub.progress < 100);
      if (firstPending) {
        return { subdimension: firstPending, test };
      }
    }

    // Buscar antes de la subdimensión actual en el test actual (si no se encontró antes)
    if (currentSubdimensionIndex !== -1) {
      for (let i = 0; i < currentSubdimensionIndex; i++) {
        if (currentSubdimensions[i].progress < 100) {
          return { subdimension: currentSubdimensions[i], test: currentTest };
        }
      }
    }

    return null;
  }

  /**
   * Cierra el modal de finalización
   */
  closeCompletionModal(): void {
    this.showCompletionModal = false;
  }

  /**
   * Continúa a la siguiente subdimensión desde el modal
   */
  continueToNextSubdimension(): void {
    this.closeCompletionModal();
    // Lógica para continuar con la siguiente subdimensión
  }

  /**
   * Finaliza el test completamente desde el modal
   */
  finishTestCompletely(): void {
    this.closeCompletionModal();
    this.generateResultsData();
    this.testInProgress = false; // Ocultar testInProgress cuando se muestran los resultados
    this.showResultsView = true;
  }

  /**
   * Verifica si todas las dimensiones están al 100%
   */
  areAllDimensionsCompleted(): boolean {
    return this.tests.every(test => test.progress === 100);
  }

  /**
   * Alterna la visualización de las matrices de resultados
   */
  toggleResultsMatrices(): void {
    this.showResultsMatrices = !this.showResultsMatrices;
  }

  /**
   * Alterna la visualización de las matrices de una dimensión específica
   */
  toggleDimensionMatrices(testId: string): void {
    if (this.expandedDimensions.has(testId)) {
      this.expandedDimensions.delete(testId);
    } else {
      this.expandedDimensions.add(testId);
    }
  }

  /**
   * Verifica si una dimensión está expandida
   */
  isDimensionExpanded(testId: string): boolean {
    return this.expandedDimensions.has(testId);
  }

  /**
   * Obtiene el ID de un test de manera segura
   */
  getTestId(test: any): string {
    return test._id || test.id || '';
  }

  /**
   * TrackBy function para dimensiones
   */
  trackByDimensionId(index: number, dimension: TestDimension): string {
    return dimension.name || index.toString();
  }

  /**
   * TrackBy function para preguntas
   */
  trackByQuestionIndex(index: number, question: Question): number {
    return index;
  }

  /**
   * Obtiene la clase CSS para el valor de brecha
   */
  getGapClass(gap: number): string {
    if (gap <= 3) return 'gap-low';
    if (gap <= 6) return 'gap-medium';
    return 'gap-high';
  }

  /**
   * Calcula la brecha de manera segura
   */
  calculateGap(question: any): number {
    const valueNow = question.answer?.valueNow || 0;
    const valueFuture = question.answer?.valueFuture || 0;
    return valueFuture - valueNow;
  }

  /**
   * Calcula el promedio del análisis presente
   */
  getAveragePresent(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => acc + (q.answer?.valueNow || 0), 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Calcula el promedio del análisis futuro
   */
  getAverageFuture(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => acc + (q.answer?.valueFuture || 0), 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Calcula el promedio de la brecha
   */
  getAverageGap(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => {
      const gap = (q.answer?.valueFuture || 0) - (q.answer?.valueNow || 0);
      return acc + gap;
    }, 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Regresa a las dimensiones
   */
  goBackToDimensions(): void {
    this.testInProgress = false;
    this.testSelected = null;
    this.selectedSubdimension = null;
    this.currentStep = 1;
    this.clearStepperCache();
  }

  /**
   * Verifica si hay una siguiente dimensión disponible
   */
  hasNextDimension(): boolean {
    if (!this.testSelected || !this.tests || this.tests.length === 0) {
      return false;
    }

    const currentTestIndex = this.tests.findIndex(
      t => t._id === this.testSelected!._id || t.id === this.testSelected!.id
    );

    return currentTestIndex !== -1 && currentTestIndex < this.tests.length - 1;
  }

  /**
   * Verifica si todas las subdimensiones de la dimensión actual están completadas al 100%
   */
  areAllSubdimensionsCompleted(): boolean {
    if (!this.testSelected || !this.subdimensions || this.subdimensions.length === 0) {
      return false;
    }

    return this.subdimensions.every(sub => sub.progress === 100);
  }

  /**
   * Navega a la siguiente dimensión (test)
   */
  goToNextDimension(): void {
    if (!this.testSelected || !this.tests || this.tests.length === 0) {
      return;
    }

    // Verificar si todas las subdimensiones están completadas
    if (!this.areAllSubdimensionsCompleted()) {
      this.showIncompleteDimensionModal = true;
      return;
    }

    const currentTestIndex = this.tests.findIndex(
      t => t._id === this.testSelected!._id || t.id === this.testSelected!.id
    );

    if (currentTestIndex === -1 || currentTestIndex >= this.tests.length - 1) {
      return;
    }

    // Navegar a la siguiente dimensión
    const nextTest = this.tests[currentTestIndex + 1];
    this.startTest(nextTest);
  }

  /**
   * Cierra el modal de dimensión incompleta y navega a la siguiente dimensión
   */
  closeIncompleteDimensionModal(): void {
    this.showIncompleteDimensionModal = false;
    
    // Navegar a la siguiente dimensión sin validar si está completa
    if (!this.testSelected || !this.tests || this.tests.length === 0) {
      console.error('No hay test seleccionado o tests disponibles');
      return;
    }

    const currentTestIndex = this.tests.findIndex(
      t => (t._id && this.testSelected!._id && t._id === this.testSelected!._id) || 
           (t.id && this.testSelected!.id && t.id === this.testSelected!.id) ||
           (t.dimensions && t.dimensions[0]?.area?.name === this.testSelected!.dimensions[0]?.area?.name)
    );

    console.log('Current test index:', currentTestIndex, 'Total tests:', this.tests.length);
    console.log('Current test:', this.testSelected?.dimensions[0]?.area?.name);
    console.log('All tests:', this.tests.map(t => t.dimensions[0]?.area?.name));

    if (currentTestIndex === -1) {
      console.error('No se encontró el test actual en la lista');
      return;
    }

    if (currentTestIndex >= this.tests.length - 1) {
      console.log('Ya está en la última dimensión');
      return;
    }

    // Limpiar el estado anterior completamente
    this.selectedSubdimension = null;
    this.showResultsView = false;
    this.resultsData = null;
    this.completedSubdimension = null;
    this.currentStep = 1;
    this.clearStepperCache();
    
    // Navegar a la siguiente dimensión
    const nextTest = this.tests[currentTestIndex + 1];
    console.log('Navegando a:', nextTest.dimensions[0]?.area?.name);
    
    // Usar setTimeout para asegurar que el cambio de detección de Angular funcione
    setTimeout(() => {
      this.startTest(nextTest);
    }, 0);
  }

  /**
   * Continúa completando la dimensión actual abriendo la primera subdimensión pendiente
   */
  continueCompletingDimension(): void {
    // Cerrar solo el modal sin navegar
    this.showIncompleteDimensionModal = false;
    
    // Encontrar la primera subdimensión pendiente (que no esté al 100%)
    if (!this.testSelected || !this.subdimensions || this.subdimensions.length === 0) {
      return;
    }

    const pendingSubdimension = this.subdimensions.find(sub => sub.progress < 100);
    
    if (pendingSubdimension) {
      // Asegurar que testInProgress esté en true para mostrar la vista de preguntas
      this.testInProgress = true;
      // Asegurar que showResultsView esté en false
      this.showResultsView = false;
      // Abrir la subdimensión pendiente
      this.startSubdimension(pendingSubdimension);
    }
  }

  /**
   * Regresa a las subdimensiones
   */
  goBackToSubdimensions(): void {
    this.selectedSubdimension = null;
    this.currentStep = 1;
    this.clearStepperCache();
  }

  /**
   * Actualiza el puntaje de una pregunta (método legacy)
   */
  updateQuestionScore(question: any, event: any): void {
    // Método legacy - mantener para compatibilidad
  }

  /**
   * Actualiza la satisfacción actual
   */
  updateCurrentSatisfaction(event: any): void {
    const newValue = parseInt(event.target.value);
    this.currentSatisfaction = newValue;
  }

  /**
   * Actualiza la satisfacción futura
   */
  updateFutureSatisfaction(event: any): void {
    const newValue = parseInt(event.target.value);
    this.futureSatisfaction = newValue;
  }

  /**
   * Descarga un PDF con todos los resultados de las dimensiones
   */
  async downloadPdf(): Promise<void> {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      let currentY = 10;
      const margin = 14;
      const contentWidth = pageWidth - (margin * 2);

      // Procesar cada test (dimensión)
      this.tests.forEach((test: Test, testIndex: number) => {
        // Verificar si necesitamos una nueva página
        if (currentY > pageHeight - 100) {
          doc.addPage();
          currentY = 10;
        }

        // Procesar cada subdimensión dentro del test
        test.dimensions.forEach((dimension: TestDimension, dimIndex: number) => {
          if (currentY > pageHeight - 80) {
            doc.addPage();
            currentY = 10;
          }

          // Barra azul oscuro con "Dimension X"
          const dimensionNumber = testIndex + 1;
          doc.setFillColor(45, 84, 118); // Azul oscuro #2d5476
          doc.rect(margin, currentY, contentWidth, 8, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(`Dimension ${dimensionNumber}`, pageWidth / 2, currentY + 5.5, { align: 'center' });
          currentY += 10;

          // Barra gris clara con nombre de la dimensión
          const dimensionName = dimension.name || test.dimensions[0]?.area?.name || `Dimensión ${dimensionNumber}`;
          doc.setFillColor(240, 240, 240); // Gris claro
          doc.rect(margin, currentY, contentWidth, 6, 'F');
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          doc.text(dimensionName, pageWidth / 2, currentY + 4, { align: 'center' });
          currentY += 8;

          // Preparar datos de la tabla para esta subdimensión
          const tableData: any[] = [];
          const isAverageRow: boolean[] = [];
          
          dimension.questions.forEach((question: Question, qIndex: number) => {
            if (question.answer) {
              const presentValue = Math.round(question.answer.valueNow || 0);
              const futureValue = Math.round(question.answer.valueFuture || 0);
              const gap = Math.round(futureValue - presentValue);
              
              tableData.push([
                question.name,
                presentValue.toString(),
                futureValue.toString(),
                gap.toString()
              ]);
              isAverageRow.push(false);
            }
          });

          // Agregar fila de promedio si hay datos
          if (tableData.length > 0) {
            const avgPresent = Math.round(this.getAveragePresent(dimension.questions));
            const avgFuture = Math.round(this.getAverageFuture(dimension.questions));
            const avgGap = Math.round(this.getAverageGap(dimension.questions));
            
            tableData.push([
              { content: 'Promedio', styles: { fontStyle: 'bold' } },
              { content: avgPresent.toString(), styles: { fontStyle: 'bold' } },
              { content: avgFuture.toString(), styles: { fontStyle: 'bold' } },
              { content: avgGap.toString(), styles: { fontStyle: 'bold', fillColor: [255, 255, 255], textColor: [0, 0, 0] } }
            ]);
            isAverageRow.push(true);
          }

          // Crear tabla si hay datos
          if (tableData.length > 0) {
            autoTable(doc, {
              head: [['Área', 'Análisis presente', 'Análisis futuro', 'Brecha por cerrar']],
              body: tableData,
              startY: currentY,
              margin: { left: margin, right: margin },
              styles: {
                fontSize: 10,
                cellPadding: 4,
                overflow: 'linebreak',
                textColor: [0, 0, 0]
              },
              headStyles: {
                fillColor: [73, 80, 87], // #495057 - Gris oscuro
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                halign: 'left',
                fontSize: 10
              },
              columnStyles: {
                0: { cellWidth: 'auto', halign: 'left' },
                1: { halign: 'center', cellWidth: 40 },
                2: { halign: 'center', cellWidth: 40 },
                3: { halign: 'center', cellWidth: 50 }
              },
              didParseCell: (data: any) => {
                const rowIndex = data.row.index;
                const colIndex = data.column.index;
                const isAverage = isAverageRow[rowIndex];

                // Colorear la brecha según su valor (solo para filas de datos, no para promedio)
                if (colIndex === 3 && !isAverage && rowIndex > 0) {
                  const gapText = data.cell.text[0] || '0';
                  const gapValue = parseInt(gapText);
                  
                  if (gapValue >= 7 && gapValue <= 9) {
                    // Brecha alta - Rojo
                    data.cell.styles.fillColor = [248, 215, 218]; // #f8d7da
                    data.cell.styles.textColor = [114, 28, 36]; // #721c24
                  } else if (gapValue >= 4 && gapValue <= 6) {
                    // Brecha media - Naranja/Melocotón
                    data.cell.styles.fillColor = [255, 243, 205]; // #fff3cd o similar a naranja claro
                    data.cell.styles.textColor = [133, 100, 4]; // #856404
                  } else if (gapValue >= 1 && gapValue <= 3) {
                    // Brecha baja - Verde
                    data.cell.styles.fillColor = [212, 237, 218]; // #d4edda
                    data.cell.styles.textColor = [21, 87, 36]; // #155724
                  } else {
                    // Sin color especial para otros valores
                    data.cell.styles.fillColor = [255, 255, 255];
                    data.cell.styles.textColor = [0, 0, 0];
                  }
                } else if (colIndex === 3 && isAverage) {
                  // El promedio no debe tener color de fondo especial
                  data.cell.styles.fillColor = [255, 255, 255];
                  data.cell.styles.textColor = [0, 0, 0];
                }
              }
            });

            // Actualizar posición Y después de la tabla
            currentY = (doc as any).lastAutoTable.finalY + 15;
          }
        });
      });

      // Guardar el PDF
      doc.save('resultados_analisis_profundo.pdf');
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      this.error('Error al generar el PDF. Por favor, inténtalo de nuevo.');
    }
  }

  /**
   * Convierte un Blob a Base64
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Limpia completamente todos los resultados marcados
   */
  clearAllResults(): void {
    console.log('[clearAllResults] Iniciando limpieza de todos los resultados');
    
    if (!confirm('¿Estás seguro de que deseas limpiar todos los resultados? Esta acción no se puede deshacer.')) {
      console.log('[clearAllResults] Usuario canceló la operación');
      return;
    }

    if (!this.authUser?.id) {
      console.error('[clearAllResults] No hay usuario autenticado');
      this.error('No se pudo identificar al usuario. Por favor, recarga la página.');
      return;
    }

    console.log('[clearAllResults] Usuario confirmó, comenzando limpieza...');
    console.log('[clearAllResults] Total de tests a limpiar:', this.tests.length);
    
    this.loader.show();
    this.isSaving = true;
    this.isClearingResults = true;

    // Recopilar todas las preguntas que necesitan ser limpiadas
    const questionsToClear: Array<{
      testId: string;
      dimensionName: string;
      questionName: string;
      priority: number;
    }> = [];

    this.tests.forEach((test: Test) => {
      const testId = test._id || test.id;
      if (!testId) {
        console.warn('[clearAllResults] Test sin ID, saltando:', test);
        return;
      }

      console.log(`[clearAllResults] Procesando test: ${testId}`);
      
      test.dimensions.forEach((dimension: TestDimension) => {
        console.log(`[clearAllResults] Procesando dimensión: ${dimension.name}`);
        
        dimension.questions.forEach((question: Question, questionIndex: number) => {
          // Solo limpiar si la pregunta tiene respuesta
          if (this.isQuestionAnswered(question)) {
            questionsToClear.push({
              testId: testId.toString(),
              dimensionName: dimension.name,
              questionName: question.name,
              priority: questionIndex + 1
            });
            console.log(`[clearAllResults] Agregada pregunta para limpiar: ${question.name} en dimensión ${dimension.name}`);
          }
        });
      });
    });

    console.log(`[clearAllResults] Total de preguntas a limpiar: ${questionsToClear.length}`);

    if (questionsToClear.length === 0) {
      console.log('[clearAllResults] No hay preguntas para limpiar');
      this.loader.hide();
      this.isSaving = false;
      this.isClearingResults = false;
      this.success('No hay resultados para limpiar.');
      return;
    }

    // Limpiar todas las preguntas secuencialmente
    let clearedCount = 0;
    const totalQuestions = questionsToClear.length;

    const clearNextQuestion = (index: number) => {
      if (index >= questionsToClear.length) {
        console.log(`[clearAllResults] Todas las preguntas limpiadas. Total: ${clearedCount}`);
        // Recargar los datos después de limpiar todo
        this.loadTests();
        this.isSaving = false;
        this.isClearingResults = false;
        this.loader.hide();
        this.success('Todos los resultados han sido limpiados exitosamente.');
        return;
      }

      const question = questionsToClear[index];
      console.log(`[clearAllResults] Limpiando pregunta ${index + 1}/${totalQuestions}: ${question.questionName} en dimensión ${question.dimensionName}`);

      const testKnowRequest: TestKnowRequest = {
        testId: question.testId,
        dimensionName: question.dimensionName,
        questionName: question.questionName,
        questionAnswer: {
          valueNow: 0,
          valueFuture: 0
        },
        priority: question.priority
      };

      this.testService.updateTestKnow(testKnowRequest).subscribe({
        next: (response) => {
          clearedCount++;
          console.log(`[clearAllResults] Pregunta ${index + 1}/${totalQuestions} limpiada exitosamente`);
          // Continuar con la siguiente pregunta
          clearNextQuestion(index + 1);
        },
        error: (error) => {
          console.error(`[clearAllResults] Error al limpiar pregunta ${index + 1}/${totalQuestions}:`, error);
          // Continuar con la siguiente pregunta incluso si hay error
          clearNextQuestion(index + 1);
        }
      });
    };

    // Iniciar el proceso de limpieza
    clearNextQuestion(0);
  }
}